<template>
    <div>
        <span class="text-lg">
            {{ $t('labels.statusQuote') }}
        </span>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-x-4 gap-y-8">
            <analytics-card
                :text="$t('labels.orderCreated')"
                :value="$formatters.formatCurrency(getStatusOrderCreate)"
                color="success"
                increase
                @click="getItems(2)" />

            <analytics-card
                :text="$t('labels.send')"
                :value="$formatters.formatCurrency(getStatusSend)"
                color="accent"
                increase
                @click="getItems(1)" />
            <analytics-card
                :text="$t('labels.init')"
                :value="$formatters.formatCurrency(getStatusInit)"
                color="error"
                increase
                @click="getItems(0)" />
            <analytics-card
                :value="$formatters.formatCurrency(getExpiredQuote)"
                :text="$t('labels.expired')"
                color="error"
                increase
                @click="getItems(-1)" />
        </div>
    </div>
    <analytics-modal
        v-model:visible="visible"
        :headers="headers"
        :loading="loading"
        :items="items" />
</template>
<script lang="ts" setup>
import AnalyticsCard from '../analytics-card.vue'
import AnalyticsModal from '../analytics-modal.vue'

import { $api, $lodash } from '#imports'
import Status from '~/components/status.vue'

const dataAnalytics = ref<{ _id: string; count: number }[]>([])

const getStatusInit = computed(() =>
  dataAnalytics.value.find((item: { [key: string]: any }) => item._id === 0)
    ? dataAnalytics.value.find((item: { [key: string]: any }) => item._id === 0)?.count
    : 0
)
const getStatusSend = computed(() =>
  dataAnalytics.value.find((item: { [key: string]: any }) => item._id === 1)
    ? dataAnalytics.value.find((item: { [key: string]: any }) => item._id === 1)?.count
    : 0
)
const getStatusOrderCreate = computed(() =>
  dataAnalytics.value.find((item: { [key: string]: any }) => item._id === 2)
    ? dataAnalytics.value.find((item: { [key: string]: any }) => item._id === 2)?.count
    : 0
)

const getExpiredQuote = computed(() =>
  dataAnalytics.value.find((item: { [key: string]: any }) => item._id === -1)
    ? dataAnalytics.value.find((item: { [key: string]: any }) => item._id === -1)?.count
    : 0
)

onMounted(async () => {
  const api = $url.dashboard.sales.getQuoteStatus
  const data: any = await $api(api)
  const resData: any = $lodash.get(data, 'data.value')
  if (resData?.success) {
    dataAnalytics.value = resData.data
  }
})

const handleViewDetail = (quote: any) => {
  if (!quote) {
    return
  }

  const routeData = useRouter().resolve({
    name: 'trucker-id-quote-management-idquote-view',
    params: { idquote: quote._id }
  })

  window.open(routeData.href, '_blank')
}

const { $formatters } = useNuxtApp()

const items = ref<Array<any>>([])
const store = $store()

const loading = ref(false)
const visible = ref(false)
const getItems = async (status: number) => {
  try {
    loading.value = true
    visible.value = true
    const api = $url.dashboard.sales.getDataQuoteStatus
    const data: any = await $api(api, { uriParams: { STATUS: status } })
    const resData: any = $lodash.get(data, 'data.value')
    if (resData?.success) {
      items.value = resData.data
    }
    loading.value = false
  } catch (e: any) {
    loading.value = false
    visible.value = false
  }
}

const headers = [
  {
    field: 'code',
    headerName: $t('labels.code'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'quoteRequestCode',
    headerName: $t('labels.quoteRequestCode'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'positions',
    headerName: $t('labels.route'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data?.positions && Array.isArray(params.data?.positions)) {
        const pluckData = $lodash.map(params.data?.positions, 'fullNameNode')

        return $lodash.join(pluckData, ' >> ')
      }
    }
  },
  {
    field: 'transportation',
    sortable: true,
    filter: 'agTextColumnFilter',
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params.data.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'customerName',
    headerName: $t('labels.customer'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'contact.fullName',
    headerName: $t('labels.contact'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'contact.email',
    headerName: $t('labels.email'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'contact.phone',
    headerName: $t('labels.phone'),
    filter: 'agTextColumnFilter',
    sortable: true,
    valueGetter: (params: any) => {
      if (params?.data?.contact?.phone) {
        return `(${params?.data?.contact.phone.code}) ${params?.data?.contact.phone.phone}`
      }
    }
  },
  {
    field: 'businessOpportunityName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.businessOpportunity'),
    sortable: true
  },
  {
    field: 'commodityName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.commodity'),
    sortable: true
  },
  {
    field: 'workflowName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.workflow'),
    sortable: true
  },
  {
    field: 'mergeContainer',
    headerName: $t('labels.mergeContainer'),
    filter: 'agTextColumnFilter',
    sortable: true,
    valueGetter: (params: any) => {
      if (params?.data?.mergeContainer) {
        return $t('labels.haveMergeContainer')
      }
      return $t('labels.notMergeContainer')
    }
  },
  {
    field: 'contCategory',
    filter: 'agNumberColumnFilter',
    headerName: $t('labels.cont'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data?.transportation === 'CONTAINER' && params.data?.contCategory) {
        if (params.data?.contCategory === 20) {
          return $t('labels.20f')
        }
        if (params.data?.contCategory === 40) {
          return $t('labels.40f')
        }
        if (params.data?.contCategory === 45) {
          return $t('labels.45f')
        }
      }
    }
  },
  {
    field: 'vehicleCategoryName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.vehicleType'),
    sortable: true
  },
  {
    field: 'headContainer',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.headContainer'),
    valueGetter: (params: any) => {
      if (params?.data?.headContainer) {
        return $t('labels.true')
      }
      return $t('labels.false')
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'quote'
    }
  },
  {
    field: 'createdAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'DataTableActions',
    cellRendererParams: {
      storeKey: 'dashboardDataTable',
      storeName: 'Dashboard',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          click: handleViewDetail,
          isAll: true
        }
      ]
    }
  }
]
</script>
